"use client"

import { useEffect, useRef, useState } from "react"

import {
  clearSummaryItemReveal,
  isSummaryItemPendingReveal,
} from "../utils/summaryReveal"

/**
 * How long Sofia appears to think before the summary starts landing. Short
 * enough not to be a wait, long enough that the summary reads as prepared for
 * this page rather than pasted in.
 */
const PREPARING_MS = 900

/** Cadence of the type-out. One frame's worth of characters per tick. */
const TYPING_TICK_MS = 16

/**
 * Total time the type-out takes, whatever the summary's length. A fixed
 * characters-per-tick rate would make a long summary crawl for many seconds;
 * pinning the duration and scaling the step keeps every summary landing in
 * about the same beat.
 */
const TYPING_DURATION_MS = 1600

interface SofiaSummaryReveal {
  /** Sofia is working: show the thinking indicator in place of the message. */
  preparing: boolean
  /** Text to render now — the full text unless mid-type. */
  text: string
  /** Mid-type: the caret belongs on the end of the line. */
  typing: boolean
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

/**
 * Play a seeded summary message in — think, then type — the first time it is
 * shown.
 *
 * Every assistant message runs this; only the ones registered by
 * `markSummaryItemForReveal` animate. Anything else (a real reply, a summary the
 * user has already watched arrive, anything rehydrated from storage) gets its
 * full text on the first render, so this costs ordinary history nothing.
 *
 * Whether to reveal is captured once at mount rather than read each render: the
 * registry entry is cleared as the animation ends, and re-reading it would flip
 * the message back to "settled" mid-type.
 *
 * Under prefers-reduced-motion the message is simply there — no thinking pause,
 * no type-out.
 */
export function useSofiaSummaryReveal(
  itemId: string,
  text: string,
): SofiaSummaryReveal {
  const [shouldReveal] = useState(
    () => isSummaryItemPendingReveal(itemId) && !prefersReducedMotion(),
  )
  const [phase, setPhase] = useState<"preparing" | "typing" | "settled">(() =>
    shouldReveal ? "preparing" : "settled",
  )
  const [typedLength, setTypedLength] = useState(0)

  // The registry entry is what makes this message animate, so it must be gone
  // before any later mount. The type-out clears it as it finishes; this covers
  // the view going away mid-play, which is equally the end of its one arrival.
  const itemIdRef = useRef(itemId)
  itemIdRef.current = itemId
  useEffect(() => {
    if (!shouldReveal) {
      return
    }
    return () => clearSummaryItemReveal(itemIdRef.current)
  }, [shouldReveal])

  useEffect(() => {
    if (phase !== "preparing") {
      return
    }
    const timer = setTimeout(() => setPhase("typing"), PREPARING_MS)
    return () => clearTimeout(timer)
  }, [phase])

  useEffect(() => {
    if (phase !== "typing") {
      return
    }
    const step = Math.max(
      1,
      Math.ceil(text.length / (TYPING_DURATION_MS / TYPING_TICK_MS)),
    )
    const timer = setInterval(() => {
      setTypedLength((current) => {
        const next = current + step
        if (next >= text.length) {
          setPhase("settled")
          clearSummaryItemReveal(itemIdRef.current)
          return text.length
        }
        return next
      })
    }, TYPING_TICK_MS)
    return () => clearInterval(timer)
  }, [phase, text])

  if (phase === "settled") {
    return { preparing: false, text, typing: false }
  }
  if (phase === "preparing") {
    return { preparing: true, text: "", typing: false }
  }
  return { preparing: false, text: text.slice(0, typedLength), typing: true }
}
